"use client";

import { useState } from "react";
import { User, Settings, LogOut, Menu, User2 } from "lucide-react";
import { usePathname } from "next/navigation";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAppSelector, useAppDispatch } from "@/features/store";
import { logout } from "@/features/slices/authSlice";

const Header = ({ toggleSidebar }: { toggleSidebar: () => void }) => {
  const pathname = usePathname();
  const { adminUser } = useAppSelector((state) => state.onboard);
  const dispatch = useAppDispatch();
  const [isLogoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Convert pathname into a readable title
  const formatTitle = (path: string) => {
    return (
      path
        .replace(/\//g, " ") // Replace slashes with spaces
        .replace(/-/g, " ") // Replace dashes with spaces
        .trim() || "Dashboard"
    ); // Default to "Dashboard" if empty
  };

  const handleLogout = () => {
    dispatch(logout());
    setLogoutDialogOpen(false);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <>
      <Card className="flex flex-row rounded-none items-center justify-between p-4 border-b shadow-md backdrop-blur-lg bg-white/70 max-sm:bg-gradient-to-r max-sm:from-gray-900 max-sm:to-gray-800">
        {/* Left: Menu Button & Page Title */}
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            className="lg:hidden -ml-3"
            onClick={toggleSidebar}
          >
            <Menu size={25} className="text-white !w-8 !h-8" />
          </Button>
          <h1 className="text-gray-900 max-sm:text-white text-xl font-semibold capitalize">
            {formatTitle(pathname)}
          </h1>
        </div>

        {/* Right: Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Avatar className="cursor-pointer max-sm:bg-white hover:scale-105 transition-transform">
              <AvatarImage
                src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${
                  process.env.NEXT_PUBLIC_IMAGE_BASE
                }_USER_IMAGE/${adminUser?.UserImage || ""}`}
                alt="User"
                className="border border-gray-200 rounded-full p-[2px]"
              />
              <AvatarFallback className="border border-gray-300 rounded-full p-1 flex items-center justify-center bg-gray-100 text-gray-600">
                <User2 size={20} />
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="bg-white shadow-lg rounded-lg w-48"
          >
            {/* Profile */}
            <DropdownMenuItem className="flex items-center space-x-2 hover:bg-gray-100 transition-colors cursor-pointer">
              <User size={18} className="text-gray-600" />
              <span className="text-gray-700">Profile</span>
            </DropdownMenuItem>

            {/* Settings */}
            <DropdownMenuItem className="flex items-center space-x-2 hover:bg-gray-100 transition-colors cursor-pointer">
              <Settings size={18} className="text-gray-600" />
              <span className="text-gray-700">Settings</span>
            </DropdownMenuItem>

            {/* Logout - Opens Dialog */}
            <DropdownMenuItem
              className="flex items-center space-x-2 bg-white text-red-600 font-medium hover:bg-red-500 hover:text-white transition-colors cursor-pointer"
              onClick={() => setLogoutDialogOpen(true)}
            >
              <LogOut
                size={18}
                className="text-red-600 group-hover:text-white"
              />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Card>

      {/* Logout Confirmation Dialog */}
      <Dialog open={isLogoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent className="bg-white shadow-lg w-sm rounded-lg p-6">
          <DialogHeader className="gap-1 text-left">
            <DialogTitle className="text-lg font-semibold">
              Confirm Logout
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">Are you sure you want to log out?</p>
          <DialogFooter className="flex justify-end space-x-2 mt-4">
            <Button
              variant="ghost"
              onClick={() => setLogoutDialogOpen(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              className="cursor-pointer bg-red-600 hover:bg-red-700 text-white"
              onClick={handleLogout}
            >
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
